<template>
  <div class="top-sports-bar" v-if="getSortedSports.length > 0">
    <div class="scroll-wrapper">
      <!-- Left arrow desktop -->
      <button
          v-if="!isSmallScreen"
          class="scroll-side-btn left"
          :class="{ active: showLeftArrow }"
          @click="scrollSports(-1)"
      >
        ‹
      </button>

      <!-- Scroll track -->
      <div class="scroll-track">
        <div class="fade-edge left" v-show="showLeftArrow"></div>
        <div class="fade-edge right" v-show="showRightArrow"></div>

        <!-- Sports scroll -->
        <div
            class="tsb-scroll"
            ref="sportsScroll"
            @scroll.passive="updateArrows"
        >
          <button
              v-for="sport in getSortedSports"
              :key="sport.id"
              :ref="`sport_${sport.id}`"
              class="tsb-sport-btn"
              :class="{ 'is-active': getOpenSportId === sport.id }"
              @click="onToggleSport(sport.id)"
          >
            <div>
              <img
                  class="tsb-sport-icon"
                  :src="`${sportPath}${sport.id}.svg`"
                  :alt="sport.name"
                  @error="e => e.target.style.display = 'none'"
              />
              <span class="tsb-sport-name">{{ sport.name }}</span>
            </div>
          </button>
        </div>
      </div>

      <!-- Right arrow desktop -->
      <button
          v-if="!isSmallScreen"
          class="scroll-side-btn right"
          :class="{ active: showRightArrow }"
          @click="scrollSports(1)"
      >
        ›
      </button>
    </div>

    <!-- Live pinned — hidden when live is disabled at the platform level. -->
    <button class="tsb-sport-btn live tsb-live-pinned" @click="goLive" v-if="!isSmallScreen && getSettings.enableLive">
      <div>
        <svg
            width="22"
            height="22"
            viewBox="0 0 22 22"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
          <!-- Dot centrale -->
          <circle cx="11" cy="11" r="2.2" fill="currentColor" />

          <!-- Arc interno sinistro -->
          <path
              d="M7.5 6.8C5.8 8.6 5.8 13.4 7.5 15.2"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              fill="none"
          >
            <animate
                attributeName="opacity"
                values="1;0.2;1"
                dur="1s"
                repeatCount="indefinite"
                begin="0s"
            />
          </path>

          <!-- Arc interno destro -->
          <path
              d="M14.5 6.8C16.2 8.6 16.2 13.4 14.5 15.2"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              fill="none"
          >
            <animate
                attributeName="opacity"
                values="1;0.2;1"
                dur="1s"
                repeatCount="indefinite"
                begin="0s"
            />
          </path>

          <!-- Arc esterno sinistro -->
          <path
              d="M4.5 4.5C1.8 7.5 1.8 14.5 4.5 17.5"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              fill="none"
          >
            <animate
                attributeName="opacity"
                values="1;0;1"
                dur="1s"
                repeatCount="indefinite"
                begin="0.25s"
            />
          </path>

          <!-- Arc esterno destro -->
          <path
              d="M17.5 4.5C20.2 7.5 20.2 14.5 17.5 17.5"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              fill="none"
          >
            <animate
                attributeName="opacity"
                values="1;0;1"
                dur="1s"
                repeatCount="indefinite"
                begin="0.25s"
            />
          </path>
        </svg>
        <span class="tsb-sport-name">{{ $t('live') }}</span>
      </div>
    </button>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

export default {
  name: 'TopSportsBar',

  data() {
    return {
      sportPath:
          process.env.VUE_APP_URL_CLOUDFRONT +
          '/svg/sports/icons-color/',
      isSmallScreen:
          typeof window !== 'undefined' && window.innerWidth <= 768,
      showLeftArrow: false,
      showRightArrow: false,
    };
  },

  computed: {
    ...mapGetters('topsports', [
      'getSortedSports',
      'getOpenSportId',
      'getActiveSportId',
      'getCategoriesBySport',
    ]),
    ...mapGetters('ui', ['getSubaction']),

    ...mapGetters({
      'getEventsDetail': 'detailPrematch/getEventsDetail',
      'getEventsLiveDetail': 'odds/getEventsLiveDetail',
      'getSettings': 'cart/getSettings',
    }),
  },

  async mounted() {
    await this.fetchSportsNavigation();

    const firstId = this.getActiveSportId;
    if (firstId) {
      this.$nextTick(() => {
        this.scrollSportIntoView(firstId);
        this.updateArrows();
      });
    }

    window.addEventListener('resize', this.onResize);
    this.$nextTick(this.updateArrows);
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
  },

  methods: {
    ...mapActions('topsports', [
      'fetchSportsNavigation',
      'toggleSport',
    ]),
    ...mapActions('ui', ['setSubaction']),
    ...mapActions({
      'removeFromDetail': 'detailPrematch/removeFromDetail',
      'removeFromPreview': 'previewPrematch/removeFromPreview',
      'removeEventLiveDetail': 'detailLive/removeEventLiveDetail',
      'setViewMode': 'ui/setViewMode',
    }),

    goLive() {
      // Close all open prematch details
      const detailSources = (this.getEventsDetail || []).map(e => e.source).filter(Boolean);
      detailSources.forEach(source => this.removeFromDetail({ source }));

      // Close all open prematch previews
      const previewSources = [...(this.getTournamentsActive || [])];
      previewSources.forEach(source => this.removeFromPreview({ source }));

      // Close all open live details
      const liveSources = [...(this.getEventsLiveDetailActive || [])];
      liveSources.forEach(source => this.removeEventLiveDetail(source));

      // Go to live section
      this.setViewMode({ mode: 'detail', section: 'live' });
      window.parent.postMessage({ type: 'liveSectionEntered' }, '*');

      window.scrollTo({ top: 0, behavior: 'smooth' });
    },

    onToggleSport(sportId) {
      const isClosing = this.getOpenSportId === sportId;

      this.toggleSport(sportId);
      this.setSubaction(isClosing ? 'home' : 'prelive');

      this.$nextTick(() => {
        this.scrollSportIntoView(sportId);
        this.updateArrows();
      });

      this.$emit('sport-selected', {
        sportId,
        categories: (this.getCategoriesBySport[sportId] || [])
            .slice()
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
      });
    },

    scrollSportIntoView(sportId) {
      const ref = this.$refs[`sport_${sportId}`];
      const el = Array.isArray(ref) ? ref[0] : ref;

      el?.scrollIntoView({
        behavior: 'smooth',
        inline: 'center',
        block: 'nearest',
      });

      setTimeout(this.updateArrows, 300);
    },

    updateArrows() {
      const el = this.$refs.sportsScroll;
      if (!el) return;

      const { scrollLeft, scrollWidth, clientWidth } = el;

      this.showLeftArrow = scrollLeft > 4;
      this.showRightArrow =
          scrollLeft + clientWidth < scrollWidth - 4;
    },

    scrollSports(direction) {
      const el = this.$refs.sportsScroll;
      if (!el) return;

      el.scrollBy({
        left: direction * 220,
        behavior: 'smooth',
      });

      setTimeout(this.updateArrows, 300);
    },

    onResize() {
      this.isSmallScreen = window.innerWidth <= 768;
      this.$nextTick(this.updateArrows);
    },
  },
};
</script>

<style scoped>
.top-sports-bar {
  width: 100%;
  display: flex;
  align-items: center;
}

.scroll-wrapper {
  flex: 1;
  position: relative;
  min-width: 0;
}

.scroll-side-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  width: 28px;
  height: 28px;
  background: var(--hsla-4);
  border: 1px solid rgba(255,255,255,0.15);
  border-radius: 50%;
  color: var(--text-platform, #fff);
  font-size: var(--font-size-8xl);
  line-height: 1;
  cursor: pointer;
  user-select: none;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s;
}

.scroll-side-btn.left  { left:  4px; }
.scroll-side-btn.right { right: 4px; }

.scroll-wrapper:hover .scroll-side-btn.active {
  opacity: 1;
  pointer-events: auto;
}

.scroll-side-btn.active:hover {
  opacity: 1;
  filter: brightness(1.2);
}

.scroll-track {
  position: relative;
  min-width: 0;
  overflow: hidden;
}

.fade-edge {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 18px;
  z-index: 2;
  pointer-events: none;
}

.fade-edge.left {
  left: 0;
  background: linear-gradient(
      to right,
      var(--bg-body, #14161d),
      transparent
  );
}

.fade-edge.right {
  right: 0;
  background: linear-gradient(
      to left,
      var(--bg-body, #14161d),
      transparent
  );
}

.tsb-scroll {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  scroll-behavior: smooth;
  scrollbar-width: none;
  padding-bottom: 2px;
}

.tsb-scroll::-webkit-scrollbar {
  display: none;
}

.tsb-sport-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 76px;
  height: 68px;
  flex-shrink: 0;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  overflow: hidden;
  background: var(--hsla-1);
  transition: transform 0.15s ease;
}

.tsb-sport-btn.tsb-live-pinned {
  background: var(--bg-card-gradient-to-r);
}
/*
.tsb-live-pinned {
  margin-left: 10px;
  background: var(--live);
}
.tsb-live-pinned .tsb-sport-name {
  background: var(--live);
}
*/
.tsb-sport-btn div {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 6px;
  width: 100%;
}

.tsb-sport-btn.is-active div {
  border-bottom: 1px solid var(--btn-background-second);
}

@media (hover: hover) {
  .tsb-sport-btn:hover {
    transform: translateY(-2px);
  }
}

.tsb-sport-icon {
  width: 22px;
  height: 22px;
  object-fit: contain;
}

.tsb-sport-name {
  font-size: var(--font-size-md);
  font-weight: 700;
  color: var(--text-platform);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 68px;
}
</style>